import type { CommentBody } from "@liveblocks/core";
import { apiClient } from "module/api";
import { useCommentsStore } from "store/comments";
import { getCachedUser } from "./mentionCache";
import { extractMentionedUserIds, flattenCommentBody } from "./mentionExtract";

type Commenter = {
  id?: string;
  name: string;
  avatar?: string;
  color?: string;
};

/**
 * Fire-and-forget helper: if the given comment body tags any users other
 * than the commenter, POST `/platform/comments/notify-mention` so the
 * backend emails them. Safe to call after every createThread / createComment —
 * it short-circuits when there are no mentions.
 */
export const notifyMentionsForBody = async (
  body: CommentBody,
  commenter: Commenter,
  threadId?: string
): Promise<void> => {
  const mentioned = extractMentionedUserIds(body);
  if (mentioned.length === 0) return;

  // Don't notify the commenter about their own @mention.
  const targets = commenter.id ? mentioned.filter((id) => id !== commenter.id) : mentioned;
  if (targets.length === 0) return;

  // Use the cached name map (populated by the inline mention renderer) to
  // produce a nicer plain-text preview: "@John Doe" instead of "@user-123".
  const bodyPreview = flattenCommentBody(body, (id) => {
    const cached = getCachedUser(id);
    return cached?.name || undefined;
  });

  const { designId, designName, designUrl } = useCommentsStore.getState().designContext;

  // NOTE: we intentionally do NOT send commenter info — the server re-derives
  // it from the authenticated user on the JWT. Anything we passed here would
  // be ignored. This prevents spoofing the sender name / avatar in the email.
  try {
    await apiClient.notifyCommentMention({
      mentionedUserIds: targets,
      designId: designId ?? undefined,
      designName: designName || "Untitled Design",
      designUrl: designUrl || (typeof window !== "undefined" ? window.location.href : ""),
      bodyPreview,
      threadId,
    });
  } catch {
    /* best-effort; never block the comment flow */
  }
};
